/**
 * Accuracy.jsx - Model performance metrics page.
 *
 * Shows the ML model's out-of-sample accuracy stats: MAE, RMSE, and the
 * "within 5 points" rate, broken down by position and plotted week by week.
 * Metrics are refreshed every 5 minutes in case training is re-run while
 * the user has the page open.
 *
 * The model is trained on 2010-2024 data and tested on held-out 2025 data
 * to produce honest out-of-sample accuracy numbers.
 */

import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { Cpu, RefreshCw, LineChart as LineChartIcon } from 'lucide-react';

const POSITION_COLORS = {
  QB:  { bg: 'bg-red-500/20',    text: 'text-red-400'    },
  RB:  { bg: 'bg-green-500/20',  text: 'text-green-400'  },
  WR:  { bg: 'bg-blue-500/20',   text: 'text-blue-400'   },
  TE:  { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  K:   { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  DST: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
};
const posStyle = (pos) => POSITION_COLORS[pos] || { bg: 'bg-white/10', text: 'text-white/60' };

/**
 * Simple SVG line chart used to display the weekly MAE and RMSE trends.
 * Renders only when there are at least 2 data points.
 */
const LineChart = ({ data, color = '#4d7cff' }) => {
  if (!data || data.length < 2) return (
    <div className="h-[180px] flex items-center justify-center text-white/20 text-sm">Not enough data to chart</div>
  );
  const width = 900, height = 180;
  const padL = 40, padR = 20, padT = 12, padB = 28;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;
  const allValues = data.map(d => d.value);
  const minV = Math.max(0, Math.min(...allValues) - 2);
  const maxV = Math.max(...allValues) + 2;
  const toX = i => data.length <= 1 ? padL + chartW / 2 : padL + (i / (data.length - 1)) * chartW;
  const toY = v => padT + chartH - ((v - minV) / (maxV - minV)) * chartH;
  const pathD = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(d.value).toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${toX(data.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)} L${toX(0).toFixed(1)},${(padT + chartH).toFixed(1)} Z`;
  const yTicks = [0, 0.5, 1].map(t => Math.round(minV + t * (maxV - minV)));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: 180 }}>
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={width - padR} y1={toY(v)} y2={toY(v)} stroke="rgba(255,255,255,0.04)" strokeWidth={1}/>
          <text x={padL - 5} y={toY(v) + 4} textAnchor="end" fontSize={9} fill="rgba(255,255,255,0.25)">{v}</text>
        </g>
      ))}
      <path d={areaD} fill={color} fillOpacity={0.06}/>
      <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={toX(i)} cy={toY(d.value)} r={2.5} fill={color}/>
          {/* Only label every Nth week to keep the axis readable */}
          {(data.length <= 20 || i % Math.ceil(data.length / 10) === 0) && (
            <text x={toX(i)} y={height - 4} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.2)">{d.label}</text>
          )}
        </g>
      ))}
    </svg>
  );
};

// Refresh the metrics display every 5 minutes
const REFRESH_MS = 5 * 60 * 1000;

const Accuracy = () => {
  const [metrics,      setMetrics]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [hasData,      setHasData]      = useState(true);
  const [metricSeason, setMetricSeason] = useState(null);

  /**
   * Checks the availability endpoint first so we don't show an error on
   * fresh installs where the model hasn't been trained yet. If data exists,
   * fetches the full metrics for the most recent season with results.
   */
  const fetchMetrics = useCallback(() => {
    api.getMetricsAvailability()
      .then(avail => {
        if (!avail?.has_data) { setHasData(false); setLoading(false); return; }
        const best = avail.seasons_with_metrics?.[0];
        setMetricSeason(best);
        return api.getMetrics(best);
      })
      .then(d => { if (d) setMetrics(d); })
      .catch(() => setHasData(false))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Pull the weekly trend data and sort by week number for the charts
  const maeTrend  = metrics?.weekly_trend?.filter(p => p.metric_name === 'MAE') .sort((a,b)=>a.week-b.week).map(p=>({label:`Wk ${p.week}`,value:p.metric_value})) || [];
  const rmseTrend = metrics?.weekly_trend?.filter(p => p.metric_name === 'RMSE').sort((a,b)=>a.week-b.week).map(p=>({label:`Wk ${p.week}`,value:p.metric_value})) || [];

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 pt-12 pb-20">

        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-5xl font-black mb-2 tracking-tight">Model <span className="text-[#4d7cff]">Accuracy</span></h1>
          <p className="text-white/40 font-medium">Out-of-sample performance · Trained on 2010–2024 · Tested on 2025</p>
        </div>

        {loading ? (
          // Skeleton loader while metrics are fetching
          <div className="space-y-4">
            {Array.from({length:4}).map((_,i)=>(
              <div key={i} className="h-20 bg-white/[0.03] rounded-2xl animate-pulse"/>
            ))}
          </div>
        ) : !hasData || !metrics ? (
          // Empty state shown before training has been run
          <div className="flex flex-col items-center justify-center py-24 gap-5">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
              <Cpu size={28} className="text-white/20"/>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold mb-2">No Model Metrics Yet</h3>
              <p className="text-white/35 text-sm max-w-sm">Metrics will appear once the ML training pipeline has been run.</p>
              <p className="text-white/20 text-xs mt-2">Run <code className="bg-white/[0.06] px-1.5 rounded">python ml/train_model.py</code></p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">

            {/* Season + training info header */}
            <div className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/[0.06] rounded-2xl">
              <div className="w-9 h-9 rounded-xl bg-[#4d7cff]/15 flex items-center justify-center flex-shrink-0">
                <Cpu size={16} className="text-[#4d7cff]"/>
              </div>
              <div>
                <div className="text-sm font-bold">{metricSeason} Season · {metrics.models_trained} Model{metrics.models_trained !== 1 ? 's' : ''} Trained</div>
                <div className="text-xs text-white/30">Back-tested on held-out historical data · PPR scoring</div>
              </div>
            </div>

            {/* Top-level accuracy headline cards */}
            {metrics.holdout_n > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Avg Error',    value: metrics.holdout_mae != null ? metrics.holdout_mae.toFixed(2) + ' pts' : '—', color: '#4d7cff', sub: 'mean absolute error' },
                  { label: 'Within 5 pts', value: metrics.holdout_within_5 != null ? metrics.holdout_within_5.toFixed(1) + '%' : '—', color: '#34d399', sub: 'of predictions' },
                  { label: 'Compared',     value: metrics.holdout_n.toLocaleString(), color: '#a78bfa', sub: 'player-weeks' },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} className="bg-[#0c1526] border border-white/[0.08] rounded-2xl p-5 relative overflow-hidden">
                    <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-10 blur-xl" style={{background:color}}/>
                    <div className="text-[10px] text-white/25 uppercase tracking-widest font-bold mb-2">{label}</div>
                    <div className="text-3xl font-black mb-1" style={{color}}>{value}</div>
                    <div className="text-[10px] text-white/20">{sub}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Per-position breakdown table */}
            {metrics.by_position?.some(p => p.holdout_n > 0) && (
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/[0.05]">
                  <div className="text-sm font-bold">Accuracy by Position</div>
                  <div className="text-[11px] text-white/30 mt-0.5">Out-of-sample — model trained on prior seasons, tested on {metricSeason}</div>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {metrics.by_position.filter(p => p.holdout_n > 0).map(p => {
                    const s = posStyle(p.position);
                    return (
                      <div key={p.position} className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg w-10 text-center flex-shrink-0 ${s.bg} ${s.text}`}>{p.position}</span>
                        <div className="flex gap-8 flex-1">
                          <div>
                            <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5">Avg Error</div>
                            <div className="text-sm font-bold text-[#4d7cff]">{p.holdout_mae != null ? p.holdout_mae.toFixed(2) + ' pts' : '—'}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5">Within 5 pts</div>
                            <div className="text-sm font-bold text-[#34d399]">{p.holdout_within_5 != null ? p.holdout_within_5.toFixed(1) + '%' : '—'}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5">Compared</div>
                            <div className="text-sm font-bold text-white/50">{p.holdout_n.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Weekly trend charts — MAE and RMSE across the season */}
            {(maeTrend.length >= 2 || rmseTrend.length >= 2) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {maeTrend.length >= 2 && (
                  <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm font-bold">MAE Over Season</div>
                        <div className="text-[11px] text-white/30">Lower is better</div>
                      </div>
                      <div className="text-xl font-black text-[#4d7cff]">{(metrics.holdout_mae ?? metrics.overall_mae)?.toFixed(2)}</div>
                    </div>
                    <LineChart data={maeTrend} color="#4d7cff"/>
                  </div>
                )}
                {rmseTrend.length >= 2 && (
                  <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm font-bold">RMSE Over Season</div>
                        <div className="text-[11px] text-white/30">Week-by-week error magnitude</div>
                      </div>
                      <div className="text-xl font-black text-[#a78bfa]">{metrics.overall_rmse?.toFixed(2)}</div>
                    </div>
                    <LineChart data={rmseTrend} color="#a78bfa"/>
                  </div>
                )}
              </div>
            )}

            {maeTrend.length < 2 && rmseTrend.length < 2 && (
              <div className="text-center text-white/20 text-xs py-6 border border-dashed border-white/[0.06] rounded-2xl">
                Weekly trend data will appear once back-testing is complete
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Accuracy;
